// vg-binding: P7.D-52
// vg-binding: P7.D-134
// vg-binding: P7.D-154
//
// apps/vendor/src/features/earnings/EarningsChart.tsx
//
// Daily earnings bar chart. UAT #2164 — upgraded from the Wave-11 inline
// <div> placeholder to Recharts. Colors ride the pw-* CSS variables so
// light/dark theme switching needs no JS. Single series → no legend
// (title carries identity); axis labels + tooltip give exact values.

import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { formatCents } from '@printway/shared/currency';

import { useTranslation } from '../../lib/i18n/index.js';
import type { TransactionRow } from '../wallet/api.js';

interface Props {
  rows: TransactionRow[];
  isLoading?: boolean;
}

function dailyAggregate(rows: TransactionRow[]): Array<{ day: string; total: number }> {
  const map = new Map<string, number>();
  for (const r of rows) {
    if (r.type !== 'earning' || r.created_at === null) continue;
    const day = new Date(r.created_at).toISOString().slice(0, 10);
    map.set(day, (map.get(day) ?? 0) + r.amount_cents);
  }
  return Array.from(map.entries())
    .map(([day, total]) => ({ day, total }))
    .sort((a, b) => a.day.localeCompare(b.day));
}

/** MM-DD — compact x tick; tooltip carries the full date. */
function shortDay(day: string): string {
  return day.slice(5);
}

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value?: number }>;
  label?: string;
}): JSX.Element | null {
  if (!active || !payload?.length) return null;
  const v = payload[0]?.value ?? 0;
  return (
    <div className="rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-xs shadow-md">
      <p className="text-pw-text-muted">{label}</p>
      <p className="font-semibold text-pw-text">{formatCents(v)}</p>
    </div>
  );
}

export function EarningsChart({ rows, isLoading }: Props): JSX.Element {
  const { t } = useTranslation('wallet');
  const series = dailyAggregate(rows);
  const maxVal = Math.max(1, ...series.map((p) => p.total));

  if (isLoading) {
    return (
      <section className="h-56 animate-pulse rounded-xl border border-pw-border bg-pw-surface" />
    );
  }

  if (series.length === 0) {
    return (
      <section className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-pw-text">{t('chart.title')}</h3>
        <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-8 text-center text-sm text-pw-text-muted">
          {t('chart.empty')}
        </p>
      </section>
    );
  }

  return (
    <section
      data-testid="earnings-chart"
      className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm"
    >
      <h3 className="mb-3 text-sm font-semibold text-pw-text">{t('chart.title')}</h3>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={series} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
          <CartesianGrid
            vertical={false}
            stroke="var(--pw-border)"
            strokeDasharray="3 3"
          />
          <XAxis
            dataKey="day"
            tickFormatter={shortDay}
            tick={{ fill: 'var(--pw-text-muted)', fontSize: 11 }}
            axisLine={{ stroke: 'var(--pw-border)' }}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tickFormatter={(v: number) => formatCents(v)}
            tick={{ fill: 'var(--pw-text-muted)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={64}
          />
          <Tooltip
            content={<ChartTooltip />}
            cursor={{ fill: 'var(--pw-surface)' }}
          />
          <Bar
            dataKey="total"
            fill="var(--pw-primary-bg)"
            radius={[4, 4, 0, 0]}
            maxBarSize={40}
          />
        </BarChart>
      </ResponsiveContainer>
      <p className="mt-2 text-xs text-pw-text-muted">
        {t('chart.summary', { count: series.length, max: formatCents(maxVal) })}
      </p>
    </section>
  );
}

export default EarningsChart;
